import type { ChartViewData } from '@shared/types/views';

/** Series colors in the Domino charter order: aubergine, coral, green, ochre, plum, ink. */
export const CHART_COLORS = ['#4a1f4e', '#ff4c33', '#1f7a43', '#8a5300', '#a07aa5', '#6a625a'] as const;

const niceStep = (rough: number): number => {
  const power = 10 ** Math.floor(Math.log10(rough));
  const fraction = rough / power;
  const nice = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : fraction <= 5 ? 5 : 10;
  return nice * power;
};

/** A value axis from 0 (or the lowest negative value) to a round maximum, with ~4 round ticks. */
export const valueScale = (data: ChartViewData): { min: number; max: number; ticks: number[] } => {
  const values = data.kind === 'stacked_bar'
    ? data.categories.map((_, index) => data.series.reduce((sum, series) => sum + Math.max(0, series.values[index] ?? 0), 0))
    : data.series.flatMap((series) => series.values.filter((value): value is number => value !== null));
  if (data.target) values.push(data.target.value);
  const low = Math.min(0, ...values);
  const high = Math.max(0, ...values);
  if (high === low) return { min: 0, max: 1, ticks: [0, 1] };
  const step = niceStep((high - low) / 4);
  const min = Math.floor(low / step) * step;
  const max = Math.ceil(high / step) * step;
  const ticks: number[] = [];
  for (let tick = min; tick <= max + step / 2; tick += step) ticks.push(Number(tick.toFixed(10)));
  return { min, max, ticks };
};

/** French number with its unit; large values shortened (12,5 k, 1,2 M) for axes. */
export const formatChartValue = (value: number, unit?: string, short = false): string => {
  const abs = Math.abs(value);
  const text = short && abs >= 1_000_000
    ? `${(value / 1_000_000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} M`
    : short && abs >= 10_000
    ? `${(value / 1_000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} k`
    : value.toLocaleString('fr-FR', { maximumFractionDigits: 2 });
  // « 65/100 » but « 450 k€ » and « 22 % ».
  return unit ? `${text}${unit.startsWith('/') ? '' : '\u00a0'}${unit}` : text;
};

/** Every n-th category label, so that labels never overlap on a narrow axis. */
export const labelEvery = (count: number, room: number): number => Math.max(1, Math.ceil(count / Math.max(1, room)));

export const hasChartValues = (data: ChartViewData): boolean =>
  data.categories.length > 0 && data.series.some((series) => series.values.some((value) => value !== null));
